import {
  createSignalFromObservable,
  type Signal,
} from '@blocksuite/affine/shared/utils';
import { LiveData, Service } from '@toeverything/infra';

import type { GlobalStateService } from '../../storage';
import type { ReasoningModeId } from './models';

const AI_REASONING_KEY = 'AIReasoning';
const AI_REASONING_EFFORT_KEY = 'AIReasoningEffort';

export type ReasoningEffortLevel = ReasoningModeId;

export function isActiveReasoningEffort(
  effort: ReasoningEffortLevel | undefined
): boolean {
  return !!effort && effort !== 'off' && effort !== 'auto';
}

/** The thinking depth an agent answers with for a reasoning switch and effort. */
export function reasoningModeOfChoice(
  enabled: boolean | undefined,
  effort: ReasoningEffortLevel | undefined
): ReasoningModeId | undefined {
  return enabled === false ? 'off' : effort;
}

export class AIReasoningService extends Service {
  constructor(private readonly globalStateService: GlobalStateService) {
    super();

    const { signal: enabled, cleanup: enabledCleanup } =
      createSignalFromObservable<boolean | undefined>(
        this._enabled$,
        undefined
      );
    this.enabled = enabled;
    this.disposables.push(enabledCleanup);

    const { signal: effort, cleanup: effortCleanup } =
      createSignalFromObservable<ReasoningEffortLevel | undefined>(
        this._effort$,
        undefined
      );
    this.effort = effort;
    this.disposables.push(effortCleanup);
  }

  enabled: Signal<boolean | undefined>;
  effort: Signal<ReasoningEffortLevel | undefined>;

  private readonly _enabled$ = LiveData.from(
    this.globalStateService.globalState.watch<boolean>(AI_REASONING_KEY),
    undefined
  );

  private readonly _effort$ = LiveData.from(
    this.globalStateService.globalState.watch<ReasoningEffortLevel>(
      AI_REASONING_EFFORT_KEY
    ),
    undefined
  );

  setEnabled = (enabled: boolean) => {
    // Without an active depth the model's own default applies (resolveReasoningMode); a stored
    // off or auto must not come back once reasoning is on again.
    if (
      enabled &&
      this.effort.value &&
      !isActiveReasoningEffort(this.effort.value)
    ) {
      this.effort.value = undefined;
      this.globalStateService.globalState.set(
        AI_REASONING_EFFORT_KEY,
        undefined
      );
    }
    // Update signal synchronously so UI reads the new value immediately
    // (GlobalState → Observable → Signal propagation is async)
    this.enabled.value = enabled;
    this.globalStateService.globalState.set(AI_REASONING_KEY, enabled);
  };

  setEffort = (effort: ReasoningEffortLevel) => {
    this.effort.value = effort;
    this.globalStateService.globalState.set(AI_REASONING_EFFORT_KEY, effort);
  };
}
